import React, { useState, useRef, useEffect } from "react";
import { FiFilter, FiX } from "react-icons/fi";
import {
  FilterButton,
  FilterDropdown,
  FilterActions,
  FilterButtonStyled,
  FilterLabel,
  FilterContainer,
} from "../styles/FilterStyles";

const StatusFilterLogic = ({
  onStatusFilterChange,
  availableStatuses,
  currentStatus,
  isDarkMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || "");
  const [alignRight, setAlignRight] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    right: 0,
  });
  const [positionReady, setPositionReady] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const scrollableParentsRef = useRef([]);

  useEffect(() => {
    setSelectedStatus(currentStatus || "");
  }, [currentStatus]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      setPositionReady(false);

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);

      scrollableParentsRef.current = [];
      let parent = containerRef.current?.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const overflow = style.overflow;
        const overflowY = style.overflowY;
        if (
          overflow === "auto" ||
          overflow === "scroll" ||
          overflowY === "auto" ||
          overflowY === "scroll"
        ) {
          scrollableParentsRef.current.push(parent);
          parent.addEventListener("scroll", handleScroll, true);
        }
        parent = parent.parentElement;
      }

      setTimeout(() => {
        if (containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const dropdownWidth = 250;
          const viewportWidth = window.innerWidth;
          const spaceOnRight = viewportWidth - containerRect.right;
          const spaceOnLeft = containerRect.left;

          const top = containerRect.bottom + 4;
          let shouldAlignRight = false;
          let left = 0;
          let right = 0;

          if (spaceOnRight < dropdownWidth && spaceOnLeft > dropdownWidth) {
            shouldAlignRight = true;
            right = viewportWidth - containerRect.right;
          } else {
            left = containerRect.left;
          }

          setAlignRight(shouldAlignRight);
          setDropdownPosition({
            top: typeof top === "number" && !isNaN(top) ? top : 0,
            left: typeof left === "number" && !isNaN(left) ? left : 0,
            right: typeof right === "number" && !isNaN(right) ? right : 0,
          });
          setPositionReady(true);
        } else {
          setDropdownPosition({
            top: 100,
            left: 100,
            right: 0,
          });
          setPositionReady(true);
        }
      }, 0);
    } else {
      setPositionReady(false);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      scrollableParentsRef.current.forEach((parent) => {
        parent.removeEventListener("scroll", handleScroll, true);
      });
      scrollableParentsRef.current = [];
    };
  }, [isOpen]);

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleApply = () => {
    // Update the main status filter state
    if (onStatusFilterChange) {
      onStatusFilterChange({ target: { value: selectedStatus } });
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedStatus("");
    // Clear the main status filter state
    if (onStatusFilterChange) {
      onStatusFilterChange({ target: { value: "" } });
    }
    setIsOpen(false);
  };

  const hasActiveFilter = currentStatus && currentStatus !== "";

  return (
    <FilterContainer ref={containerRef}>
      <FilterButton
        active={hasActiveFilter}
        onClick={() => setIsOpen(!isOpen)}
        title="Filter by status"
      >
        <FiFilter />
        {hasActiveFilter && <FiX size={12} />}
      </FilterButton>

      {isOpen && positionReady && (
        <FilterDropdown
          ref={dropdownRef}
          alignRight={alignRight}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          right={dropdownPosition.right}
          isDarkMode={isDarkMode}
        >
          <FilterLabel isDarkMode={isDarkMode}>Order Status</FilterLabel>

          <select
            value={selectedStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={{
              width: "100%",
              padding: "0.5rem",
              border: isDarkMode ? "1px solid #4a5568" : "1px solid #e1e5ee",
              borderRadius: "6px",
              fontSize: "0.9rem",
              marginBottom: "1rem",
              cursor: "pointer",
              background: isDarkMode ? "#2d3748" : "white",
              color: isDarkMode ? "#e2e8f0" : "#333",
            }}
          >
            <option value="">All Statuses</option>
            {availableStatuses?.map((status) => (
              <option key={status} value={status}>
                {status.split(/[-_]/).length > 1
                  ? status.split(/[-_]/).slice(1).join("-")
                  : status}
              </option>
            ))}
          </select>

          <FilterActions>
            <FilterButtonStyled className="clear" onClick={handleClear} isDarkMode={isDarkMode}>
              Clear
            </FilterButtonStyled>
            <FilterButtonStyled
              className="apply"
              onClick={handleApply}
              disabled={!selectedStatus}
              isDarkMode={isDarkMode}
            >
              Apply
            </FilterButtonStyled>
          </FilterActions>
        </FilterDropdown>
      )}
    </FilterContainer>
  );
};

export default StatusFilterLogic;
